import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import {
  Building2,
  ChevronRight,
  Home,
  Store,
  Tablet,
  User,
  UserRound,
  type LucideIcon,
} from "lucide-react";
import { SectionCard } from "@/components/common";
import type { AdminBookingDetailResponse } from "@/services/bookingService";
import { formatCommissionPercentLabel } from "@/lib/commissionRate";
import { formatMoney } from "@/lib/money";
import { cn } from "@/lib/utils";
import type { BookingListRow } from "./bookingViewModel";

type Tone = "primary" | "accent" | "violet" | "sky" | "success" | "surface";

const TONE_CLASS: Record<Tone, string> = {
  primary: "bg-primary-50 text-primary-600 ring-primary-600/15",
  accent: "bg-accent-50 text-accent-600 ring-accent-600/15",
  violet: "bg-violet-50 text-violet-600 ring-violet-600/15",
  sky: "bg-sky-50 text-sky-600 ring-sky-600/15",
  success: "bg-success-50 text-success-600 ring-success-600/15",
  surface: "bg-surface-100 text-surface-500 ring-surface-500/15",
};

interface Commission {
  rate: number | null;
  amountCents: number | null;
}

function commissionFor(
  detail: AdminBookingDetailResponse,
  party: AdminBookingDetailResponse["commissions"][number]["party"],
): Commission | null {
  const entry = detail.commissions.find((c) => c.party === party);
  return entry ? { rate: entry.rate, amountCents: entry.amount_cents } : null;
}

function commissionLabel({ rate, amountCents }: Commission): string {
  const parts: string[] = [];
  if (rate != null) parts.push(formatCommissionPercentLabel(rate));
  if (amountCents != null) parts.push(formatMoney(amountCents));
  return parts.length > 0 ? parts.join(" · ") : "—";
}

function CommissionPill({ label, value }: { label?: string; value: Commission }) {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-surface-100 px-1.5 py-0.5 text-xs font-medium tabular-nums text-surface-700 dark:bg-surface-200/60">
      {label ? <span className="font-normal text-surface-500">{label}</span> : null}
      {commissionLabel(value)}
    </span>
  );
}

interface PartyRowProps {
  icon: LucideIcon;
  tone: Tone;
  role: string;
  name: ReactNode;
  to?: string | null;
  aside?: ReactNode;
  muted?: boolean;
}

function PartyRow({ icon: Icon, tone, role, name, to, aside, muted }: PartyRowProps) {
  const body = (
    <>
      <span
        className={cn(
          "flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset",
          TONE_CLASS[muted ? "surface" : tone],
        )}
        aria-hidden
      >
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-xs font-medium text-surface-500">{role}</span>
        <span
          className={cn(
            "block text-sm",
            muted ? "text-surface-500" : "truncate font-medium text-surface-900",
            to && "group-hover:text-primary-700",
          )}
        >
          {name}
        </span>
        {aside ? <span className="mt-1 flex flex-wrap gap-1">{aside}</span> : null}
      </span>
      {to ? (
        <ChevronRight
          className="h-4 w-4 shrink-0 text-surface-300 transition-transform group-hover:translate-x-0.5 group-hover:text-surface-500"
          aria-hidden
        />
      ) : null}
    </>
  );

  const rowClass = "flex items-center gap-3 px-5 py-3";
  return (
    <li>
      {to ? (
        <Link
          to={to}
          className={cn(
            rowClass,
            "group transition-colors hover:bg-surface-50 dark:hover:bg-surface-100/50",
          )}
        >
          {body}
        </Link>
      ) : (
        <div className={rowClass}>{body}</div>
      )}
    </li>
  );
}

interface BookingAdminPartiesProps {
  row: BookingListRow;
  detail: AdminBookingDetailResponse;
}

/** Side panel listing every party on the booking (activity, seller, structure, host, agent, device) with commissions. */
export function BookingAdminParties({ row, detail }: BookingAdminPartiesProps) {
  const { t } = useTranslation("bookings");
  const booking = detail.booking;
  const partner = detail.partner;
  const host = detail.host;
  const salesAgent = detail.sales_agent_activity;
  const ownerId = partner?.user_id ?? detail.activity.user_id;
  const hostUserId = booking.host_user_id ?? host?.user_id ?? null;
  const activityName = row.activityName || detail.activity.name;

  const sellerCommission = commissionFor(detail, "seller");
  const hostCommission = commissionFor(detail, "host");
  const saActivityCommission = commissionFor(detail, "sales_agent_activity");
  const saHouseCommission = commissionFor(detail, "sales_agent_house");

  return (
    <SectionCard
      title={t("adminDetail.sections.parties")}
      icon={Building2}
      contentClassName="p-0 sm:p-0"
    >
      <ul className="divide-y divide-surface-200/70">
        <PartyRow
          icon={Building2}
          tone="primary"
          role={t("adminDetail.partner.activity")}
          name={activityName || "—"}
          to={activityName ? `/activities/${booking.activity_id}` : null}
        />
        <PartyRow
          icon={Store}
          tone="accent"
          role={t("adminDetail.partner.name")}
          name={partner?.name ?? "—"}
          muted={!partner}
          to={
            partner
              ? partner.role === "HOST"
                ? `/hosts/${ownerId}`
                : `/sellers/${ownerId}`
              : null
          }
          aside={
            partner?.email || sellerCommission ? (
              <>
                {sellerCommission ? <CommissionPill value={sellerCommission} /> : null}
                {partner?.email ? (
                  <span className="truncate text-xs text-surface-500">
                    {partner.email}
                  </span>
                ) : null}
              </>
            ) : null
          }
        />
        <PartyRow
          icon={Home}
          tone="violet"
          role={t("detail.hostingStructure")}
          name={
            booking.house_id
              ? (booking.house_name ?? booking.house_id)
              : t("adminDetail.structure.none")
          }
          muted={!booking.house_id}
          to={booking.house_id ? `/houses/${booking.house_id}` : null}
        />
        {hostUserId ? (
          <PartyRow
            icon={User}
            tone="sky"
            role={t("adminDetail.structure.host")}
            name={host?.name ?? hostUserId}
            to={`/hosts/${hostUserId}`}
            aside={hostCommission ? <CommissionPill value={hostCommission} /> : null}
          />
        ) : null}
        <PartyRow
          icon={UserRound}
          tone="success"
          role={t("adminDetail.salesAgent.name")}
          name={
            salesAgent ? salesAgent.sales_agent_email : t("adminDetail.salesAgent.none")
          }
          muted={!salesAgent}
          to={
            salesAgent
              ? `/admin/sales-agents/${salesAgent.sales_agent_user_id}`
              : null
          }
          aside={
            saActivityCommission || saHouseCommission ? (
              <>
                {saActivityCommission ? (
                  <CommissionPill
                    label={t("adminDetail.commissions.activityShort")}
                    value={saActivityCommission}
                  />
                ) : null}
                {saHouseCommission ? (
                  <CommissionPill
                    label={t("adminDetail.commissions.houseShort")}
                    value={saHouseCommission}
                  />
                ) : null}
              </>
            ) : null
          }
        />
        <PartyRow
          icon={Tablet}
          tone="surface"
          role={t("detail.generatingDevice")}
          name={row.kioskName ?? t("detail.unnamedDevice")}
          to={`/devices?deviceId=${booking.device_id}`}
        />
      </ul>
    </SectionCard>
  );
}
